import { Navigate, type RouteObject } from 'react-router-dom'
import { Layout } from '@/components/Layout'
import { ProtectedRoute } from '@/components/ProtectedRoute'
import { AuthCallbackPage } from '@/pages/AuthCallback'
import { RequestCenterDashboard } from '@/features/dashboard'
import { SwitchUserPage } from '@/pages/dev/SwitchUser'
import { RequireRole } from '@/components/RequireRole'
import { UserRole } from '@kfolks/types'
import { LoginPage } from '@/pages/Login'
import { NotFoundPage } from '@/pages/NotFound'
import { AccountInactivePage } from '@/pages/errors/AccountInactive'
import { UnauthorizedPage } from '@/pages/errors/Unauthorized'
import { ForbiddenPage } from '@/pages/errors/Forbidden'
import { PermissionsPage } from '@/features/users'
import { ProvidersPage } from '@/features/providers'
import { UsersPage } from '@/features/users'
import { EntraDirectoryPage } from '@/features/people'
import { EmployeesPage } from '@/features/people'
import { ContractorsPage } from '@/features/people'
import { MasterDataPage } from '@/features/master-data'
import { ClientsPage } from '@/features/organization'
import { ProjectsPage } from '@/features/organization'
import { IdmAccMappingsPage } from '@/features/users'
import { IdmRequestsPage } from '@/features/users'
import { SettingsPage } from '@/features/settings'
import { FilesPage } from '@/features/files'
import { ProviderDetailPage } from '@/features/providers'
import { ProjectDetailPage } from '@/features/organization'
import {
	AdminRequestsPage,
	ApprovalDetailPage,
	ApprovalsInboxPage,
	AuditSearchPage,
	CatalogPage,
	DataSourcesPage,
	MyRequestsPage,
	NewRequestPage,
	RequestDetailPage,
	RequestTypesPage,
	SystemsPage,
} from '@/features/request-center'
import { FormBuilderPage } from '@/modules/form-builder/FormBuilderPage'
import { WorkflowBuilderPage } from '@/modules/workflow-builder/WorkflowBuilderPage'
import { AdminTicketsPage } from '@/features/tickets'

export const routes: RouteObject[] = [
	{
		path: '/login',
		element: <LoginPage />,
	},
	{
		// Must be outside ProtectedRoute — this page handles the OAuth redirect
		// before the auth cookie has been set by the backend.
		path: '/auth/callback',
		element: <AuthCallbackPage />,
	},
	{
		path: '/error/inactive',
		element: <AccountInactivePage />,
	},
	{
		path: '/error/unauthorized',
		element: <UnauthorizedPage />,
	},
	{
		path: '/error/forbidden',
		element: <ForbiddenPage />,
	},
	{
		path: '/',
		element: (
			<ProtectedRoute>
				<Layout />
			</ProtectedRoute>
		),
		children: [
			{
				// Spec 4 / Group 11 — legacy `DashboardPage` retired. The
				// admin/USER pre-Spec-4 root is now a redirect so external
				// bookmarks pointing at `/` still land somewhere useful.
				index: true,
				element: <Navigate to="/dashboard" replace />,
			},
			{
				// Spec 0 / Spec 4: post-login landing for every authenticated
				// role. The full role-adaptive Request Center dashboard
				// (Spec 4 / Group 9). VIEWER sees the collapsed surface;
				// USER / ADMIN / SUPER_ADMIN see the role-adaptive grid.
				path: 'dashboard',
				element: <RequestCenterDashboard />,
			},
			// Dev-only user impersonation (ADR-0009, Spec 0 Group 11).
			// Registered only on non-prod stages; admin-gated by <RequireRole>.
			...(import.meta.env.VITE_APP_STAGE !== 'prod'
				? [
						{
							path: 'dev/switch-user',
							element: (
								<RequireRole anyOf={[UserRole.ADMIN, UserRole.SUPER_ADMIN]}>
									<SwitchUserPage />
								</RequireRole>
							),
						},
					]
				: []),
			// Non-RC platform-admin routes — gated to SUPER_ADMIN only per the
			// ADMIN-RC-only / SUPER_ADMIN-platform split (ADR-0009). ADMIN is
			// scoped to Request Center; everything below is platform-grade.
			{
				path: 'users',
				element: (
					<RequireRole minRole={UserRole.SUPER_ADMIN}>
						<UsersPage />
					</RequireRole>
				),
			},
			{
				path: 'entra-directory',
				element: (
					<RequireRole minRole={UserRole.SUPER_ADMIN}>
						<EntraDirectoryPage />
					</RequireRole>
				),
			},
			{
				path: 'employees',
				element: (
					<RequireRole minRole={UserRole.SUPER_ADMIN}>
						<EmployeesPage />
					</RequireRole>
				),
			},
			{
				path: 'contractors',
				element: (
					<RequireRole minRole={UserRole.SUPER_ADMIN}>
						<ContractorsPage />
					</RequireRole>
				),
			},
			{
				path: 'providers',
				element: (
					<RequireRole minRole={UserRole.SUPER_ADMIN}>
						<ProvidersPage />
					</RequireRole>
				),
			},
			{
				path: 'providers/:id',
				element: (
					<RequireRole minRole={UserRole.SUPER_ADMIN}>
						<ProviderDetailPage />
					</RequireRole>
				),
			},
			{
				path: 'permissions',
				element: (
					<RequireRole minRole={UserRole.SUPER_ADMIN}>
						<PermissionsPage />
					</RequireRole>
				),
			},
			{
				path: 'clients',
				element: (
					<RequireRole minRole={UserRole.SUPER_ADMIN}>
						<ClientsPage />
					</RequireRole>
				),
			},
			{
				path: 'projects',
				element: (
					<RequireRole minRole={UserRole.SUPER_ADMIN}>
						<ProjectsPage />
					</RequireRole>
				),
			},
			{
				path: 'projects/:id',
				element: (
					<RequireRole minRole={UserRole.SUPER_ADMIN}>
						<ProjectDetailPage />
					</RequireRole>
				),
			},
			{
				path: 'idm-requests',
				element: (
					<RequireRole minRole={UserRole.SUPER_ADMIN}>
						<IdmRequestsPage />
					</RequireRole>
				),
			},
			{
				path: 'idm-acc-mappings',
				element: (
					<RequireRole minRole={UserRole.SUPER_ADMIN}>
						<IdmAccMappingsPage />
					</RequireRole>
				),
			},
			{
				path: 'master-data',
				element: (
					<RequireRole minRole={UserRole.SUPER_ADMIN}>
						<MasterDataPage />
					</RequireRole>
				),
			},
			{
				path: 'files',
				element: (
					<RequireRole minRole={UserRole.SUPER_ADMIN}>
						<FilesPage />
					</RequireRole>
				),
			},
			{
				path: 'settings',
				element: (
					<RequireRole minRole={UserRole.SUPER_ADMIN}>
						<SettingsPage />
					</RequireRole>
				),
			},
			// Request Center — user-facing routes (Spec 1 G14). Order matters:
			// `/requests-center/new/:slug` and `/requests-center/my` come before
			// `/requests-center/:id` so the catch-all doesn't shadow them.
			{
				path: 'requests-center/catalog',
				element: <CatalogPage />,
			},
			{
				path: 'requests-center/new/:slug',
				element: <NewRequestPage />,
			},
			{
				path: 'requests-center/my',
				element: <MyRequestsPage />,
			},
			{
				// Keep this literal path BEFORE the :id catch-all so "approvals"
				// doesn't match and try to fetch itself as a UUID.
				path: 'requests-center/approvals',
				element: <ApprovalsInboxPage />,
			},
			{
				path: 'requests-center/approvals/:id',
				element: <ApprovalDetailPage />,
			},
			// Admin-wide requests view — backs the Admin Snapshot dashboard
			// tiles (Provisioning, Stale, Productive errors deep-link here
			// with URL filter params). Registered BEFORE
			// `requests-center/:id` so the catch-all doesn't capture
			// "admin" as an id.
			{
				path: 'requests-center/admin/requests',
				element: (
					<RequireRole minRole={UserRole.ADMIN}>
						<AdminRequestsPage />
					</RequireRole>
				),
			},
			{
				path: 'requests-center/:id',
				element: <RequestDetailPage />,
			},
			// Request Center — admin pages (Spec 1 G15). Each is wrapped in
			// <RequireRole minRole={UserRole.ADMIN}> per ADR-0009.
			{
				path: 'admin/request-center/types',
				element: (
					<RequireRole minRole={UserRole.ADMIN}>
						<RequestTypesPage />
					</RequireRole>
				),
			},
			{
				path: 'admin/request-center/types/:id/form',
				element: (
					<RequireRole minRole={UserRole.ADMIN}>
						<FormBuilderPage />
					</RequireRole>
				),
			},
			{
				path: 'admin/request-center/types/:id/workflow',
				element: (
					<RequireRole minRole={UserRole.ADMIN}>
						<WorkflowBuilderPage />
					</RequireRole>
				),
			},
			{
				path: 'admin/request-center/data-sources',
				element: (
					<RequireRole minRole={UserRole.ADMIN}>
						<DataSourcesPage />
					</RequireRole>
				),
			},
			{
				path: 'admin/request-center/systems',
				element: (
					<RequireRole minRole={UserRole.ADMIN}>
						<SystemsPage />
					</RequireRole>
				),
			},
			{
				path: 'admin/request-center/audit',
				element: (
					<RequireRole minRole={UserRole.ADMIN}>
						<AuditSearchPage />
					</RequireRole>
				),
			},
			{
				path: 'admin/request-center/tickets',
				element: (
					<RequireRole minRole={UserRole.ADMIN}>
						<AdminTicketsPage />
					</RequireRole>
				),
			},
		],
	},
	{
		path: '*',
		element: <NotFoundPage />,
	},
]
